/*
 * The public shell.
 *
 * It exists for two reasons. The first is the header and footer chrome - which is
 * also why the kb/* and releasenotes/* catch-alls render inside this layout rather
 * than beside it: a dead link is still a reader on the site, and they get the
 * header, the footer and a way back. The second is the bootstrap fetch: the site
 * row, and the products and category tags the browse page renders its filters
 * from. Fetching that once here rather than in each page means the filter panel
 * does not flicker when a reader moves from the list to an article and back. (The
 * release-note list is the exception that proves the point: its product box is
 * counted by release notes, not articles, so it comes with the list's own
 * response - S42.)
 *
 * The header ends with the shared SiteNav: Articles, Release Notes (S42,
 * 2026-10-12), Manage when the server confirms the reader may administer, and Sign
 * in or Sign out (2026-10-09). The way in is
 * offered to every visitor: hiding it would be security theatre - the route exists
 * either way, and the server is what refuses - while showing it is how an
 * administrator gets in.
 *
 * Moving to another page inside the public site renews an expired sign-in, as a
 * page load does (useRenewOnNavigation, 2026-10-15).
 *
 * The language dropdown sits just left of the light/dark switch (S48,
 * 2026-10-29), and every word this shell and its pages print comes from the
 * chosen language's dictionary (i18n/).
 */

import { NavLink, Outlet } from 'react-router-dom';
import { createContext, useContext, useEffect, useMemo } from 'react';
import type { PublicBootstrap } from '@accotool-kb/shared';
import { getBootstrap } from '../api/publicApi.js';
import { forReader } from '../api/publicReader.js';
import { usePublicReader } from '../auth/AuthProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import { ErrorBanner, Loading } from '../components/ui.js';
import { SiteBrand, useSiteBranding } from '../site/SiteBranding.js';
import { SiteFooter } from '../site/SiteFooter.js';
import { ColorSchemeSwitch } from '../site/ColorSchemeSwitch.js';
import { LanguageSwitch } from '../site/LanguageSwitch.js';
import { SiteNav } from '../site/SiteNav.js';
import { useRenewOnNavigation } from '../auth/useRenewOnNavigation.js';
import { useMessages } from '../i18n/language.js';

interface PublicContextValue {
  bootstrap: PublicBootstrap;
}

const PublicContext = createContext<PublicContextValue | null>(null);

export function usePublicContext(): PublicContextValue {
  const context = useContext(PublicContext);
  if (!context) throw new Error('usePublicContext must be used inside PublicLayout.');
  return context;
}

export function PublicLayout(): JSX.Element {
  // For the reader (S46): the filters count what THIS reader can read, so the
  // bootstrap waits for a sign-in being restored and is read again when the
  // reader changes.
  const reader = usePublicReader();
  const bootstrapState = useAsync(() => forReader(reader, () => getBootstrap(reader)), [reader.ready, reader.key]);
  const branding = useSiteBranding();
  const t = useMessages();
  useRenewOnNavigation();

  const value = useMemo<PublicContextValue | null>(
    () => (bootstrapState.data ? { bootstrap: bootstrapState.data } : null),
    [bootstrapState.data],
  );

  // The bootstrap carries the site's name and icon so first paint stays one
  // request; hand them to the provider both shells read from.
  useEffect(() => {
    if (bootstrapState.data) branding.setSite(bootstrapState.data.site);
  }, [bootstrapState.data, branding]);

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t.common.skipToContent}
      </a>

      <header className="site-header">
        <div className="site-header-inner">
          <NavLink to="/" className="brand">
            <SiteBrand />
          </NavLink>

          <span className="header-spacer" />

          <LanguageSwitch />

          <ColorSchemeSwitch />

          <SiteNav shell="public" />
        </div>
      </header>

      <main id="main">
        <div className="page">
          {bootstrapState.loading && !bootstrapState.data && (
            <Loading label={t.layout.loadingKnowledgeBase} />
          )}

          {bootstrapState.error && (
            <ErrorBanner error={bootstrapState.error} onRetry={bootstrapState.reload} />
          )}

          {value && (
            <PublicContext.Provider value={value}>
              <Outlet />
            </PublicContext.Provider>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
